import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { isBefore } from 'date-fns/isBefore';
import { isAfter } from 'date-fns/isAfter';
import { isSameDay } from 'date-fns/isSameDay';
import PropertyModel from 'uplisting-frontend/models/property';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';
import BookingModel from 'uplisting-frontend/models/booking';
import EnquiryModel from 'uplisting-frontend/models/enquiry';
import BookingRequestModel from 'uplisting-frontend/models/booking-request';
import UnavailabilityModel from 'uplisting-frontend/models/unavailability';
import { formatDateQuery } from 'uplisting-frontend/utils';
import { OccasionRecord } from 'uplisting-frontend/services/calendar/-internals/record';
import RateModel from 'uplisting-frontend/models/rate';
import ChangeoverModel from 'uplisting-frontend/models/changeover';
import { RowOption } from 'uplisting-frontend/pods/xcalendar/controller';

export type LocationType = PropertyModel | MultiUnitModel;

export type PropertyRowDataItem =
  | BookingModel
  | EnquiryModel
  | BookingRequestModel
  | UnavailabilityModel;

type CacheForLookupInner<T> = Record<string, T>;
type CacheForLookup<T> = Record<string, CacheForLookupInner<T>>;

export type CacheLookupItem =
  | RateModel
  | ChangeoverModel
  | OccasionRecord[]
  | undefined;

export default class calendarData extends Service {
  @service store!: Services['store'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @cached @tracked rateCacheForLookup: CacheForLookup<RateModel> = {};
  @cached @tracked changeoverCacheForLookup: CacheForLookup<ChangeoverModel> =
    {};
  @cached @tracked propertyCacheForLookup: CacheForLookup<OccasionRecord[]> =
    {};
  @cached @tracked multiUnitCacheForLookup: CacheForLookup<OccasionRecord[]> =
    {};

  @tracked private cacheRevision = 0;

  public resetState(): void {
    this.store.unloadAll('rate');
    this.store.unloadAll('changeover');
    this.store.unloadAll('calendar');
    this.store.unloadAll('booking-request');
    this.store.unloadAll('booking');
    this.store.unloadAll('changeover');
    this.store.unloadAll('enquiry');
    this.store.unloadAll('unavailability');

    this.#incrementCacheRevision();
  }

  public setupFasterLookup(): void {
    this.#clearCaches();

    this.#setupRateCaches();
    this.#setupChangeoverCaches();
    this.#setupOccasionCaches();
    this.#incrementCacheRevision();
  }

  /**
   * get cached/optimized data for specific record
   */
  public getCacheFor(
    field: RowOption,
    date: Date,
    id: string,
  ): CacheLookupItem {
    const dateFormatted = this.#getSecondLevelCacheKey(date);

    switch (field) {
      case RowOption.properties:
        return this.propertyCacheForLookup[id]?.[dateFormatted];
      case RowOption.multiUnits:
        return this.multiUnitCacheForLookup[id]?.[dateFormatted];
      case RowOption.changeovers:
        return this.changeoverCacheForLookup[id]?.[dateFormatted];
      default:
        return this.rateCacheForLookup[id]?.[dateFormatted];
    }
  }

  #clearCaches(): void {
    this.rateCacheForLookup = {};
    this.changeoverCacheForLookup = {};
    this.propertyCacheForLookup = {};
    this.multiUnitCacheForLookup = {};

    this.#incrementCacheRevision();
  }

  // Update cache for a specific occasion
  #updateOccasionCache(
    location: LocationType,
    date: Date,
    record: OccasionRecord,
  ): void {
    const cacheKey = this.#getFirstLevelCacheKey(location);
    const dateKey = this.#getSecondLevelCacheKey(date);

    const cache =
      location instanceof MultiUnitModel
        ? this.multiUnitCacheForLookup
        : this.propertyCacheForLookup;

    if (!cache[cacheKey]) {
      cache[cacheKey] = {};
    }

    if (!cache[cacheKey][dateKey]) {
      cache[cacheKey][dateKey] = [];
    }

    cache[cacheKey][dateKey].push(record);
    this.#incrementCacheRevision();
  }

  // setup occasion caches for bookings, enquiries, booking requests, and unavailabilities
  #setupOccasionCaches(): void {
    const bookings = this.#getAllFor('booking');
    const enquiries = this.#getAllFor('enquiry');
    const bookingRequests = this.#getAllFor('booking-request');
    const unavailabilities = this.#getAllFor('unavailability');

    this.#setupCacheForOccasions(bookings);
    this.#setupCacheForOccasions(enquiries);
    this.#setupCacheForOccasions(bookingRequests);
    this.#setupCacheForOccasions(unavailabilities);
  }

  // setup cache for a specific type of occasions
  #setupCacheForOccasions(occasions: PropertyRowDataItem[]): void {
    const { dates, startDate, endDate } = this.calendarManager.viewport;

    occasions.forEach((occasion) => {
      const { property } = occasion;
      const multiUnit = 'multiUnit' in occasion ? occasion.multiUnit : null;

      const record = new OccasionRecord(this, occasion);

      const overlapsViewport = this.#occasionOverlapsViewport(
        record.meta.startDate,
        record.meta.endDate,
        startDate,
        endDate,
      );

      // Check if occasion overlaps with viewport
      if (!overlapsViewport) {
        return;
      }

      dates.forEach((date) => {
        // Cache occasion if it overlaps with this specific date
        const overlapsDates = this.#occasionOverlapsDate(
          record.meta.startDate,
          record.meta.endDate,
          date,
        );

        if (!overlapsDates) {
          return;
        }
        // For ongoing events (start before viewport), cache them only on the first date
        const isOngoingEvent = isBefore(record.meta.startDate, startDate);
        const isFirstDateInViewport = isSameDay(date, startDate);

        // Cache on the actual start date OR on the first viewport date for ongoing events
        const shouldCache =
          isSameDay(record.meta.startDate, date) ||
          (isOngoingEvent && isFirstDateInViewport);

        if (!shouldCache) {
          return;
        }

        if (multiUnit) {
          this.#updateOccasionCache(multiUnit, date, record);
        } else if (property) {
          this.#updateOccasionCache(property, date, record);
        }
      });
    });
  }

  /**
   * Check if an occasion overlaps with the viewport date range
   */
  #occasionOverlapsViewport(
    occasionStart: Date,
    occasionEnd: Date,
    viewportStart: Date,
    viewportEnd: Date,
  ): boolean {
    // Occasion overlaps if:
    // 1. Occasion starts before viewport ends AND
    // 2. Occasion ends after viewport starts
    return (
      (isBefore(occasionStart, viewportEnd) ||
        isSameDay(occasionStart, viewportEnd)) &&
      (isAfter(occasionEnd, viewportStart) ||
        isSameDay(occasionEnd, viewportStart))
    );
  }

  /**
   * Check if an occasion overlaps with a specific date
   */
  #occasionOverlapsDate(
    occasionStart: Date,
    occasionEnd: Date,
    date: Date,
  ): boolean {
    return (
      (isBefore(occasionStart, date) || isSameDay(occasionStart, date)) &&
      (isAfter(occasionEnd, date) || isSameDay(occasionEnd, date))
    );
  }

  #getAllFor(modelName: string): PropertyRowDataItem[] {
    return this.store.peekAll(modelName).slice();
  }

  #getFirstLevelCacheKey(model: LocationType): string {
    return model.id;
  }

  #getSecondLevelCacheKey(date: Date): string {
    return formatDateQuery(date);
  }

  #incrementCacheRevision(): void {
    this.cacheRevision++;
  }

  #setupRateCaches(): void {
    this.#setupFasterLookupFor('rate', 'date', 'rateCacheForLookup');
  }

  #setupChangeoverCaches(): void {
    this.#setupFasterLookupFor(
      'changeover',
      'scheduledAt',
      'changeoverCacheForLookup',
    );
  }

  #setupFasterLookupFor(
    modelName: string,
    dateField: string,
    cacheField: 'rateCacheForLookup' | 'changeoverCacheForLookup',
  ): void {
    const records = this.store.peekAll(modelName).slice();
    const { dates } = this.calendarManager.viewport;

    const cache = {};

    for (const record of records) {
      const { id } = record.property;

      for (const date of dates) {
        const dateFormatted = this.#getSecondLevelCacheKey(date);
        const recordDateFormatted = this.#getSecondLevelCacheKey(
          record[dateField],
        );

        if (dateFormatted !== recordDateFormatted) {
          continue;
        }

        if (!cache[id]) {
          cache[id] = {};
        }

        (cache[id] as CacheForLookupInner<RateModel | ChangeoverModel>)[
          recordDateFormatted
        ] = record;
      }
    }

    this[cacheField] = cache;
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/data': calendarData;
  }
}
